import { __ } from '@wordpress/i18n';
import { createBrowserHistory } from 'history';
import PropTypes from 'prop-types';
import qs from 'qs';
import React, { Fragment, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

// Config.
import { HISTORY_SHAPE } from '../../../config/prop-types';

// Components.
import Filters from '../filters';
import ResearchItem from '../researchItem';

// Services.
import fetchAll from '../../js/services/fetch-all';
import filterResearchNetwork
  from '../../js/services/filters/filter-research-network';
import filtersFromResearchNetwork
  from '../../js/services/filters/filters-from-research-network';

const ResearchNetwork = ({
  history,
  initialSlugs,
}) => {
  // Config.
  const loadMoreAmount = 5;

  // State.
  const [activeFilters, setActiveFilters] = useState([]);
  const [activeSlugs, setActiveSlugs] = useState(initialSlugs);
  const [contentEndIndex, setContentEndIndex] = useState(loadMoreAmount);
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [research, setResearch] = useState([]);
  const [researchFiltered, setResearchFiltered] = useState([]);

  /**
   * Turns date string provided as meta value into JavaScript Date object for sorting purposes.
   *
   * @param {string} datestring - Date string to format.
   */
  const formatDate = (datestring) => {
    const yyyy = datestring.substr(0, 4);
    const mm = datestring.substr(4, 2);
    const dd = datestring.substr(6, 2);
    switch (datestring.length) {
      case 4:
        return new Date(`${yyyy}`);
      case 6:
        return new Date(`${yyyy}-${mm}`);
      case 8:
        return new Date(`${yyyy}-${mm}-${dd}`);
      default:
        return 0;
    }
  };

  /**
   * Toggle a slug from active to inactive or inactive to active.
   *
   * @param {string} slug - Slug to toggle.
   */
  const handleActiveSlug = (slug) => {
    const hasSlug = activeSlugs.includes(slug);
    const newActiveSlugs = hasSlug
      ? activeSlugs.filter((string) => string !== slug)
      : [
        ...activeSlugs,
        slug,
      ];
    setActiveSlugs(newActiveSlugs);

    // Push the change onto URL history.
    if (0 === newActiveSlugs.length) {
      history.push('/knight-research-network/', { slug: '' });
    } else {
      const params = {
        slug: newActiveSlugs.join(','),
      };
      history.push(`?${qs.stringify(params)}`, { slug });
    }
  };

  // Sort items by publish date in reverse chron order, pushing potential null values to the bottom
  function sortForPublishDate(aa, bb) {
    if (! aa.meta.knight_foundation_krn_research_publish_date) {
      return 1;
    } else if (! bb.meta.knight_foundation_krn_research_publish_date) {
      return - 1;
    }
    return formatDate(bb.meta.knight_foundation_krn_research_publish_date) - formatDate(aa.meta.knight_foundation_krn_research_publish_date); // eslint-disable-line max-len
  }

  /**
   * Updates the filtered content list and list of available filters in response
   * to data loading or active filters being applied.
   * @param {array} nextResearch - The new research array to filter.
   * @param {array} nextActiveFilters - The new active filters array to apply.
   */
  const updateFilters = (nextResearch, nextActiveFilters) => {
    const nextResearchFiltered = filterResearchNetwork(
      nextResearch,
      nextActiveFilters
    );
    const nextResearchFilteredSorted = nextResearchFiltered.sort(sortForPublishDate); // eslint-disable-line max-len
    setResearchFiltered(nextResearchFilteredSorted);
    setFilters(filtersFromResearchNetwork(nextResearchFilteredSorted));
    setContentEndIndex(loadMoreAmount);
  };

  // Update results when filters change.
  useEffect(() => updateFilters(research, activeFilters), [activeFilters]);

  // Fetch all posts on mount.
  useEffect(() => {
    (async () => {
      const allPosts = await fetchAll(['krn-research']);
      const { 'krn-research': researchData } = allPosts;
      setResearch(researchData);
      updateFilters(researchData, activeFilters);
      setLoading(false);
    })();
  }, []);

  return (
    <div className="content-listing">
      <h2>
        {__(
          'Browse Research by Network Members',
          'knight-foundation-app'
        )}
      </h2>
      {loading ? (
        <div className="content-listing__loading">
          {__('Loading...', 'knight-foundation-app')}
        </div>
      ) : (
        <div className="content-listing__panel">
          <div className="content-listing__panel-content">
            <Fragment>
              {researchFiltered.length ? (
                <Fragment>
                  <ul className="content-listing__content-list">
                    {researchFiltered.slice(0, contentEndIndex)
                      .map((item) => (
                        <ResearchItem
                          history={history}
                          activeSlug={activeSlugs}
                          item={item}
                          key={item.id}
                          setActiveSlug={handleActiveSlug}
                        />
                      ))}
                  </ul>
                  {researchFiltered.length > contentEndIndex &&
                    <div className="content-listing__more">
                      <button
                        onClick={() =>
                          setContentEndIndex(contentEndIndex + loadMoreAmount)
                        }
                      >
                        {__('Load More', 'knight-foundation-app')}
                      </button>
                    </div>
                  }
                </Fragment>
              ) : (
                <div className="content-listing__noresults">
                  {__(
                    'There aren\'t any reports matching those filters.',
                    'knight-foundation-app'
                  )}
                </div>
              )}
            </Fragment>
          </div>
          <div className="research-listing__filters research__filters">
            <Filters
              activeFilters={activeFilters}
              enableKeywordSearch
              enableResetButton
              filters={filters}
              initialOpen={false}
              onChange={setActiveFilters}
            />
          </div>
        </div>
      )}
    </div>
  );
};

ResearchNetwork.propTypes = {
  initialSlugs: PropTypes.arrayOf(PropTypes.string).isRequired,
  history: PropTypes.shape(HISTORY_SHAPE).isRequired,
};

const appElement = document.querySelector('.research-network');
if (appElement) {
  // Extract initial state from URL, if available.
  const { location: { search: queryString = '' } = {} } = window;

  // Get topic/stage.
  const {
    slug: initialSlugRaw = '',
  } = qs.parse(queryString.replace('?', '')) || {};

  const initialSlugs = initialSlugRaw.split(',');

  // Create history with react router.
  const customHistory = createBrowserHistory();

  // Bootstrap the app.
  ReactDOM.render(React.createElement(ResearchNetwork, {
    initialSlugs,
    history: customHistory,
  }), appElement);
}
